import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const RED   = '#bf1e2e';
const BG    = '#0a0f1a';
const SURF  = '#0d1525';
const BORD  = 'rgba(255,255,255,0.07)';
const DIM   = 'rgba(255,255,255,0.45)';

// ─── Stage + prompt content ────────────────────────────────────────────────────

const STAGES = [
  {
    id: 1,
    name: 'Foundation and Fundamentals',
    ages: '8 and under',
    color: '#bf1e2e',
    weeks: [
      {
        week: 1,
        title: 'My Sport',
        prompts: [
          'What is your favourite sport or activity? Describe what it feels like when you are playing it.',
          'What is the best moment you have had in your sport so far? Tell us what happened.',
          'What do you want to get better at in your sport this season?',
        ],
      },
      {
        week: 2,
        title: 'How I Show Up',
        prompts: [
          'How do you show up for your team? Give one real example of something you do that helps the people around you.',
          'What does being a good teammate mean to you? Give one example from your own life.',
          'When something is hard or frustrating in your sport, what do you do to keep going?',
        ],
      },
      {
        week: 3,
        title: 'My Strengths',
        prompts: [
          'Name one thing you are really good at in your sport. How did you get good at it?',
          'Name one thing you are good at that has nothing to do with sports.',
          'What is one thing you are still learning? What are you doing to improve?',
        ],
      },
    ],
  },
  {
    id: 2,
    name: 'Discovery',
    ages: '9 to 11',
    color: '#f0a500',
    weeks: [
      {
        week: 1,
        title: 'My Athlete Identity',
        prompts: [
          'Describe yourself as an athlete in 3 words. What does each of those words mean to you?',
          'What makes you unique on your team? What do you bring that no one else does?',
          'How has playing your sport changed you as a person?',
        ],
      },
      {
        week: 2,
        title: 'Beyond the Court',
        prompts: [
          'What do you love doing when you are not playing your sport? How does it connect to who you are?',
          'Who is someone you look up to, on or off the court? What do you admire most about them?',
          'What is something important to you that most people would not know just from watching you play?',
        ],
      },
      {
        week: 3,
        title: 'What I Am Proud Of',
        prompts: [
          'Describe a moment in your sport where you were proud of how you competed, not just the result.',
          'Describe a moment off the court where you showed up as the person you want to be.',
          'What do you want people to say about you at the end of this season, as an athlete and as a person?',
        ],
      },
    ],
  },
  {
    id: 3,
    name: 'Profile and Branding',
    ages: '12 to 13',
    color: '#0fa86d',
    weeks: [
      {
        week: 1,
        title: 'My Athlete Profile',
        prompts: [
          'Write 3 sentences that describe who you are as an athlete and as a person. Make it sound like you.',
          'What are the 3 most important things you want people to know about you beyond your sport?',
          'If you could only keep one achievement from this year, on or off the court, which would it be and why?',
        ],
      },
      {
        week: 2,
        title: 'My Off-Court Strengths',
        prompts: [
          'List your top 3 strengths that have nothing to do with your sport. For each one, give a real example of when you showed that strength.',
          'Describe a time you solved a problem, helped someone, or stepped up as a leader. What did you do and what happened?',
          'What skill or quality do you have that you think will matter more as you get older? Why?',
        ],
      },
      {
        week: 3,
        title: 'My Story Online',
        prompts: [
          'What story do you want your name to tell? Think about what you would want someone to know about you from how you show up, both online and in person. What values would they see?',
          'What is one thing you stand for that you would never want to be misrepresented? Why does it matter to you?',
          'If your athlete identity had a tagline, what would it be? Write one sentence that captures who you are and what you stand for.',
        ],
      },
    ],
  },
  {
    id: 4,
    name: 'NIL Awareness and Readiness',
    ages: '14 to 17',
    color: '#014c8d',
    weeks: [
      {
        week: 1,
        title: 'My Athlete Bio',
        prompts: [
          'Write a short athlete bio of 3 to 4 sentences. Include your sport, your strengths, your values, and what you stand for beyond the game.',
          'What are 3 qualities about you that a school, organization, or brand would value most? Back each one up with a real example.',
          'How would you describe your athletic career so far to someone who has never seen you play? What would you want them to understand?',
        ],
      },
      {
        week: 2,
        title: 'My Brand Values',
        prompts: [
          'If a brand wanted to partner with you, what would you want to represent? List 3 values you would not compromise on and explain why each one matters.',
          'What kinds of partnerships or opportunities would you turn down, even if they paid well? What does that choice say about what you stand for?',
          'Name one athlete whose personal brand you respect and explain what they do right. What can you bring from that into your own story?',
        ],
      },
      {
        week: 3,
        title: 'My Personal Brand',
        prompts: [
          'Describe your personal brand in one sentence. What do you want to be known for at 18, at 25, and beyond?',
          'What is the gap between who you are right now and the athlete or person you are building toward? What is your plan to close it?',
          'Write the first paragraph of the bio you will have at 18. Include sport, character, values, and the impact you plan to make.',
        ],
      },
    ],
  },
];

// ─── Trait unlocks for Stage 1 & 2 ────────────────────────────────────────────
// Index: [week-1][task-1] → [trait, description]

const TRAIT_UNLOCKS: Record<number, [string, string][][]> = {
  1: [
    [
      ['Athlete', 'You put yourself in the game.'],
      ['Competitor', 'You know what your best looks like.'],
      ['Always Growing', 'You have your eyes on what is next.'],
    ],
    [
      ['Team Presence', 'You make the people around you better.'],
      ['Good Teammate', 'You understand what team actually means.'],
      ['Resilient', 'You keep going when things get hard.'],
    ],
    [
      ['Skilled', 'You have put in the work to earn this.'],
      ['More Than an Athlete', 'Your abilities go beyond the game.'],
      ['Self-Aware', 'You know who you are and where you are going.'],
    ],
  ],
  2: [
    [
      ['Original', 'No one on your team is exactly like you.'],
      ['Differentiator', 'You know what you bring that others do not.'],
      ['Changed by the Game', 'Sport has shaped who you are.'],
    ],
    [
      ['Well-Rounded', 'Your identity is bigger than your sport.'],
      ['Inspired', 'You know who you want to become.'],
      ['Depth', 'There is more to you than anyone sees on game day.'],
    ],
    [
      ["Competitor's Heart", 'You compete for the right reasons.'],
      ['Character', 'You show up the same way on and off the court.'],
      ['Future-Focused', 'You are already thinking about who you are becoming.'],
    ],
  ],
};

// ─── Profile unlocks for Stage 3 & 4 ─────────────────────────────────────────
// Index: [week-1][task-1] → [milestone title, description]

const PROFILE_UNLOCKS: Record<number, [string, string][][]> = {
  3: [
    [
      ['Your Athlete Voice', 'Your identity as an athlete now has words.'],
      ['What People Know', 'You have named what matters beyond your sport.'],
      ['Your Defining Moment', 'The achievement that shaped this season is now part of your story.'],
    ],
    [
      ['Off-Court Strength', 'Your strengths outside of sport are on record.'],
      ['Your Leadership Story', 'You have shown up when it counted.'],
      ['What You Are Building', 'The quality that will carry you is identified.'],
    ],
    [
      ['Your Story Online', 'You know the story your name should tell.'],
      ['What You Stand For', 'Your non-negotiable is locked in.'],
      ['Your Athlete Tagline', 'One sentence. Your whole standpoint. That is yours.'],
    ],
  ],
  4: [
    [
      ['Your Athlete Bio', 'Your introduction to the world is ready.'],
      ['Your Proven Value', 'You can back up what you bring with real examples.'],
      ['Your Journey Told', 'You can speak to your own story with confidence.'],
    ],
    [
      ['Your Brand Values', 'Your non-negotiables are on record.'],
      ['Your Integrity', 'You know what you will say no to.'],
      ['Your Inspiration', 'You are learning from athletes who built something.'],
    ],
    [
      ['Your Personal Brand', 'One sentence. Three horizons. That is a brand.'],
      ['Your Gap Identified', 'Knowing where you are going is the first step to getting there.'],
      ['NIL Ready', 'You have done what most athletes never stop to do.'],
    ],
  ],
};

// ─── Interfaces ────────────────────────────────────────────────────────────────

interface Progress {
  id: string;
  current_stage: number;
  current_week: number;
  current_task_order: number;
  stage_1_completed: boolean;
  stage_2_completed: boolean;
  stage_3_completed: boolean;
  stage_4_completed: boolean;
}

interface SavedResponse { stage: number; week: number; task_order: number; response: string; }

interface UnlockItem {
  type: 'trait' | 'profile';
  title: string;
  description: string;
  responsePreview?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function rgbFor(color: string) {
  const map: Record<string, string> = {
    '#bf1e2e': '191,30,46',
    '#f0a500': '240,165,0',
    '#0fa86d': '15,168,109',
    '#014c8d': '1,76,141',
  };
  return map[color] ?? '191,30,46';
}

function getPromptFontSize(stageId: number): number {
  if (stageId === 1) return 20;
  if (stageId === 2) return 18;
  return 15;
}

function getUnlock(stage: number, week: number, task: number, response: string): UnlockItem {
  if (stage <= 2) {
    const data = TRAIT_UNLOCKS[stage]?.[week - 1]?.[task - 1];
    return { type: 'trait', title: data?.[0] ?? 'Complete', description: data?.[1] ?? 'Well done.' };
  }
  const data = PROFILE_UNLOCKS[stage]?.[week - 1]?.[task - 1];
  return {
    type: 'profile',
    title: data?.[0] ?? 'Profile Updated',
    description: data?.[1] ?? 'Your profile is growing.',
    responsePreview: response.length > 100 ? response.slice(0, 100) + '…' : response,
  };
}

// ─── Component ─────────────────────────────────────────────────────────────────

export default function EarlyPathwaysPortalPage() {
  const navigate = useNavigate();

  const [userId, setUserId] = useState<string | null>(null);
  const [fullName, setFullName] = useState('');
  const [institutionName, setInstitutionName] = useState('');
  const [epStage, setEpStage] = useState<number>(1);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [responses, setResponses] = useState<SavedResponse[]>([]);

  const [activeWeek, setActiveWeek] = useState(1);
  const [activeTaskOrder, setActiveTaskOrder] = useState(1);
  const [draftText, setDraftText] = useState('');
  const [savedThisTask, setSavedThisTask] = useState(false);
  const [saving, setSaving] = useState(false);

  const [showUnlock, setShowUnlock] = useState(false);
  const [currentUnlock, setCurrentUnlock] = useState<UnlockItem | null>(null);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [downloadingCard, setDownloadingCard] = useState(false);

  const profileCardRef = useRef<HTMLDivElement>(null);

  // ─── Load ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    const load = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate('/early-pathways/login'); return; }
      setUserId(user.id);

      const [{ data: prof }, { data: prog }, { data: resp }] = await Promise.all([
        supabase.from('profiles').select('full_name, user_type, institution_id, ep_stage').eq('id', user.id).single(),
        supabase.from('ep_progress').select('*').eq('user_id', user.id).single(),
        supabase.from('ep_responses').select('stage, week, task_order, response').eq('user_id', user.id),
      ]);

      if (prof?.user_type !== 'early_pathways') {
        await supabase.auth.signOut();
        navigate('/early-pathways/login');
        return;
      }

      setFullName(prof?.full_name ?? '');
      const stage = prof?.ep_stage ?? 1;
      setEpStage(stage);

      if (prof?.institution_id) {
        const { data: inst } = await supabase.from('institutions').select('name').eq('id', prof.institution_id).single();
        setInstitutionName(inst?.name ?? '');
      }

      if (prog) {
        setProgress(prog);
        setActiveWeek(prog.current_week ?? 1);
        setActiveTaskOrder(prog.current_task_order ?? 1);
      }

      if (resp) setResponses(resp);
      setLoading(false);
    };
    load();
  }, [navigate]);

  // ─── Hydrate draft when task changes ───────────────────────────────────────

  useEffect(() => {
    const existing = responses.find(r =>
      r.stage === epStage && r.week === activeWeek && r.task_order === activeTaskOrder
    );
    setDraftText(existing?.response ?? '');
    setSavedThisTask(!!existing);
    setShowUnlock(false);
    setCurrentUnlock(null);
  }, [activeWeek, activeTaskOrder, epStage, responses]);

  // ─── Progress helpers ──────────────────────────────────────────────────────

  const isTaskComplete = (weekNum: number, taskOrder: number) =>
    responses.some(r => r.stage === epStage && r.week === weekNum && r.task_order === taskOrder);

  const isWeekComplete = (weekNum: number) =>
    [1, 2, 3].every(t => isTaskComplete(weekNum, t));

  const isWeekUnlocked = (weekNum: number) => {
    if (weekNum === 1) return true;
    return isWeekComplete(weekNum - 1);
  };

  const goToWeek = (weekNum: number) => {
    if (!isWeekUnlocked(weekNum)) return;
    setActiveWeek(weekNum);
    const firstIncomplete = [1, 2, 3].find(t => !isTaskComplete(weekNum, t)) ?? 1;
    setActiveTaskOrder(firstIncomplete);
    setSidebarOpen(false);
  };

  const totalComplete = responses.filter(r => r.stage === epStage).length;

  // ─── Save ──────────────────────────────────────────────────────────────────

  const handleSave = useCallback(async () => {
    if (!userId || !draftText.trim() || !progress) return;
    setSaving(true);

    const stageData = STAGES.find(s => s.id === epStage)!;
    const prompt = stageData.weeks[activeWeek - 1].prompts[activeTaskOrder - 1];
    const responseText = draftText.trim();

    await supabase.from('ep_responses').upsert({
      user_id: userId,
      stage: epStage,
      week: activeWeek,
      task_order: activeTaskOrder,
      prompt,
      response: responseText,
      submitted_at: new Date().toISOString(),
    }, { onConflict: 'user_id,stage,week,task_order' });

    setResponses(prev => [
      ...prev.filter(r => !(r.stage === epStage && r.week === activeWeek && r.task_order === activeTaskOrder)),
      { stage: epStage, week: activeWeek, task_order: activeTaskOrder, response: responseText },
    ]);

    const updates: Record<string, any> = { last_active: new Date().toISOString() };
    if (activeTaskOrder < 3) {
      updates.current_task_order = activeTaskOrder + 1;
    } else if (activeWeek < 3) {
      updates.current_week = activeWeek + 1;
      updates.current_task_order = 1;
    } else {
      updates[`stage_${epStage}_completed`] = true;
      fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/ep-stage-complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, stage: epStage, full_name: fullName, institution_name: institutionName }),
      }).catch(() => {});
    }

    const { data: updatedProg } = await supabase
      .from('ep_progress').update(updates).eq('id', progress.id).select().single();
    if (updatedProg) setProgress(updatedProg);

    const unlock = getUnlock(epStage, activeWeek, activeTaskOrder, responseText);
    setSaving(false);
    setSavedThisTask(true);
    setCurrentUnlock(unlock);
    setShowUnlock(true);
  }, [userId, draftText, progress, epStage, activeWeek, activeTaskOrder, fullName, institutionName]);

  const handleAdvance = () => {
    if (activeTaskOrder < 3) {
      setActiveTaskOrder(t => t + 1);
    } else if (activeWeek < 3) {
      setActiveWeek(w => w + 1);
      setActiveTaskOrder(1);
    }
  };

  const handleContinue = () => {
    setShowUnlock(false);
    setCurrentUnlock(null);
    handleAdvance();
  };

  const handleDownloadCard = async () => {
    if (!profileCardRef.current || downloadingCard) return;
    setDownloadingCard(true);
    try {
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(profileCardRef.current, {
        backgroundColor: '#0a0f1a',
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const link = document.createElement('a');
      link.download = `bthd-early-pathways-${fullName.toLowerCase().replace(/\s+/g, '-')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Card download failed:', err);
    } finally {
      setDownloadingCard(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/early-pathways/login');
  };

  // ─── Render guards ─────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: DIM, fontFamily: 'Barlow, sans-serif', fontSize: 14 }}>Loading…</div>
      </div>
    );
  }

  const stageData = STAGES.find(s => s.id === epStage) ?? STAGES[0];
  const weekData = stageData.weeks[activeWeek - 1];
  const currentPrompt = weekData.prompts[activeTaskOrder - 1];
  const stageColor = stageData.color;
  const rgb = rgbFor(stageColor);

  const isLastTask = activeTaskOrder === 3;
  const isLastWeek = activeWeek === 3;
  const allComplete = (progress?.[`stage_${epStage}_completed` as keyof Progress] as boolean) ?? false;
  const canSave = draftText.trim().length > 10 && !savedThisTask;
  const promptFontSize = getPromptFontSize(epStage);

  const continueLabel = (isLastTask && isLastWeek) ? 'FINISH' : isLastTask ? 'NEXT WEEK →' : 'NEXT QUESTION →';

  // ─── Sidebar ──────────────────────────────────────────────────────────────

  const SidebarContent = (
    <div style={{
      width: 240, flexShrink: 0, background: SURF,
      borderRight: `1px solid ${BORD}`,
      display: 'flex', flexDirection: 'column',
      height: '100vh', position: 'sticky', top: 0, overflow: 'auto',
    }}>
      {/* Brand */}
      <div style={{ padding: '20px 20px 16px', borderBottom: `1px solid ${BORD}` }}>
        <img
          src="/bthd-email-sig-300x80-transparent.png"
          alt="Bigger Than Hoop Dreams"
          style={{ height: 24, width: 'auto', filter: 'brightness(0) invert(1)', marginBottom: 8, display: 'block' }}
        />
        <div style={{
          display: 'inline-flex', alignItems: 'center',
          background: 'rgba(191,30,46,0.08)', border: `1px solid ${RED}`,
          borderRadius: 30, padding: '3px 12px', marginBottom: 6,
        }}>
          <span style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, fontSize: 10, color: RED, letterSpacing: 2 }}>EARLY PATHWAYS</span>
        </div>
        {institutionName && <div style={{ fontSize: 11, color: DIM, marginTop: 2 }}>{institutionName}</div>}
      </div>

      {/* Athlete */}
      <div style={{ padding: '14px 20px', borderBottom: `1px solid ${BORD}` }}>
        <div style={{ fontSize: 10, color: DIM, letterSpacing: 1.5, fontFamily: 'Barlow Condensed, sans-serif', marginBottom: 2 }}>ATHLETE</div>
        <div style={{ fontSize: 14, color: '#fff', fontWeight: 600 }}>{fullName || 'Athlete'}</div>
      </div>

      {/* Program */}
      <div style={{ padding: '14px 20px', borderBottom: `1px solid ${BORD}` }}>
        <div style={{ fontSize: 10, color: DIM, letterSpacing: 1.5, fontFamily: 'Barlow Condensed, sans-serif', marginBottom: 6 }}>YOUR PROGRAM</div>
        <span style={{
          fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, fontSize: 10,
          letterSpacing: 1.5, color: stageColor,
          background: `rgba(${rgb}, 0.15)`, padding: '2px 7px', borderRadius: 4,
        }}>STAGE {epStage}</span>
        <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, fontSize: 14, color: '#fff', lineHeight: 1.3, marginTop: 6, marginBottom: 2 }}>
          {stageData.name}
        </div>
        <div style={{ fontSize: 11, color: DIM }}>Ages {stageData.ages}</div>
      </div>

      {/* Week nav */}
      <nav style={{ padding: '12px 0' }}>
        {stageData.weeks.map(w => {
          const unlocked = isWeekUnlocked(w.week);
          const complete = isWeekComplete(w.week);
          const active = w.week === activeWeek;
          const tasksDone = [1, 2, 3].filter(t => isTaskComplete(w.week, t)).length;

          return (
            <button
              key={w.week}
              onClick={() => goToWeek(w.week)}
              style={{
                width: '100%', textAlign: 'left', padding: '11px 20px',
                background: active ? `rgba(${rgb}, 0.08)` : 'none',
                border: 'none', borderLeft: active ? `3px solid ${stageColor}` : '3px solid transparent',
                cursor: unlocked ? 'pointer' : 'default',
                opacity: unlocked ? 1 : 0.3,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, color: active ? stageColor : DIM, fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: 1, marginBottom: 2 }}>
                    WEEK {w.week}
                  </div>
                  <div style={{ fontSize: 12, color: active ? '#fff' : 'rgba(255,255,255,0.55)', fontWeight: active ? 600 : 400 }}>
                    {w.title}
                  </div>
                  {unlocked && (
                    <div style={{ display: 'flex', gap: 4, marginTop: 5 }}>
                      {[1, 2, 3].map(t => (
                        <div key={t} style={{
                          width: 7, height: 7, borderRadius: '50%',
                          background: isTaskComplete(w.week, t) ? stageColor : 'rgba(255,255,255,0.12)',
                        }} />
                      ))}
                    </div>
                  )}
                </div>
                {complete && <span style={{ color: stageColor, fontSize: 12, marginTop: 1 }}>✓</span>}
                {!unlocked && <span style={{ color: DIM, fontSize: 10, marginTop: 1 }}>locked</span>}
                {unlocked && !complete && (
                  <span style={{ fontSize: 10, color: DIM, marginTop: 1, whiteSpace: 'nowrap' }}>{tasksDone}/3</span>
                )}
              </div>
            </button>
          );
        })}
      </nav>

      {/* Stage 1 & 2: Trait shelf */}
      {(epStage === 1 || epStage === 2) && (
        <div style={{ padding: '14px 20px', borderTop: `1px solid ${BORD}` }}>
          <div style={{ fontSize: 10, color: DIM, letterSpacing: 1.5, fontFamily: 'Barlow Condensed, sans-serif', marginBottom: 10 }}>
            YOUR TRAITS
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {TRAIT_UNLOCKS[epStage]?.flatMap((weekTraits, wIdx) =>
              weekTraits.map((taskTrait, tIdx) => {
                const earned = isTaskComplete(wIdx + 1, tIdx + 1);
                return (
                  <span key={`${wIdx}-${tIdx}`} style={{
                    fontSize: 10, padding: '3px 8px', borderRadius: 20,
                    background: earned ? `rgba(${rgb}, 0.18)` : 'rgba(255,255,255,0.04)',
                    color: earned ? stageColor : 'rgba(255,255,255,0.15)',
                    border: `1px solid ${earned ? stageColor : 'rgba(255,255,255,0.06)'}`,
                    fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, letterSpacing: 0.5,
                    transition: 'all 0.3s',
                  }}>
                    {taskTrait[0]}
                  </span>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Stage 3 & 4: Profile card */}
      {(epStage === 3 || epStage === 4) && (
        <div style={{ padding: '14px 20px', borderTop: `1px solid ${BORD}` }}>
          <div style={{ fontSize: 10, color: DIM, letterSpacing: 1.5, fontFamily: 'Barlow Condensed, sans-serif', marginBottom: 10 }}>
            YOUR PROFILE
          </div>
          {stageData.weeks.map(w => {
            const complete = isWeekComplete(w.week);
            const unlocked = isWeekUnlocked(w.week);
            const weekResponses = responses
              .filter(r => r.stage === epStage && r.week === w.week)
              .sort((a, b) => b.task_order - a.task_order);
            const latestResponse = weekResponses[0];

            return (
              <div key={w.week} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  <div style={{
                    fontSize: 10, fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: 0.8,
                    color: complete ? stageColor : unlocked ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.2)',
                  }}>
                    {w.title.toUpperCase()}
                  </div>
                  {complete && <span style={{ color: stageColor, fontSize: 9 }}>✓</span>}
                </div>
                {latestResponse ? (
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', lineHeight: 1.4, fontStyle: 'italic' }}>
                    "{latestResponse.response.length > 60 ? latestResponse.response.slice(0, 60) + '…' : latestResponse.response}"
                  </div>
                ) : (
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.15)', fontStyle: 'italic' }}>
                    {unlocked ? 'In progress…' : 'Not yet unlocked'}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Sign out */}
      <div style={{ marginTop: 'auto', padding: '16px 20px', borderTop: `1px solid ${BORD}` }}>
        <button onClick={handleLogout} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', fontSize: 12, cursor: 'pointer', fontFamily: 'Barlow, sans-serif' }}>
          Sign out
        </button>
      </div>
    </div>
  );

  // ─── Journey complete ──────────────────────────────────────────────────────

  if (allComplete && !reviewMode) {
    return (
      <div style={{ minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Barlow, sans-serif', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 480 }}>
          <div style={{
            width: 80, height: 80, borderRadius: 20, margin: '0 auto 24px',
            background: `rgba(${rgb}, 0.1)`, border: `2px solid ${stageColor}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 32, color: stageColor }}>9</div>
          </div>
          <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 36, color: '#fff', letterSpacing: 1, marginBottom: 8 }}>
            STAGE COMPLETE
          </div>
          <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, fontSize: 16, color: stageColor, letterSpacing: 1, marginBottom: 16 }}>
            Stage {epStage}: {stageData.name}
          </div>

          {/* Trait or profile summary on completion */}
          {(epStage === 1 || epStage === 2) && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 11, color: DIM, fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: 1.5, marginBottom: 12 }}>
                TRAITS EARNED
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center' }}>
                {TRAIT_UNLOCKS[epStage]?.flatMap((weekTraits, wIdx) =>
                  weekTraits.map((taskTrait, tIdx) => (
                    <span key={`${wIdx}-${tIdx}`} style={{
                      fontSize: 12, padding: '4px 12px', borderRadius: 20,
                      background: `rgba(${rgb}, 0.18)`, color: stageColor,
                      border: `1px solid ${stageColor}`,
                      fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, letterSpacing: 0.5,
                    }}>
                      {taskTrait[0]}
                    </span>
                  ))
                )}
              </div>
            </div>
          )}

          {epStage === 4 ? (
            <>
              <div style={{ fontSize: 15, color: DIM, lineHeight: 1.8, marginBottom: 32 }}>
                {fullName}, you built your athlete bio, locked in your brand values, and wrote the personal brand statement that will carry you into everything that comes next. That work is yours.
                <br /><br />
                Your next chapter is the BTHD Behavioural Program, available through your organization.
              </div>
              <div style={{ height: 3, borderRadius: 2, background: `linear-gradient(to right, ${RED}, ${stageColor})`, marginBottom: 36 }} />

              {/* Profile card */}
              <div ref={profileCardRef} style={{
                width: 480, maxWidth: '100%', background: '#0a0f1a',
                borderRadius: 20, padding: '28px 28px 24px',
                border: '1px solid rgba(255,255,255,0.08)',
                textAlign: 'left', margin: '0 auto 20px',
                boxSizing: 'border-box',
              }}>
                {/* Card header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                  <img
                    src="/bthd-email-sig-300x80-transparent.png"
                    alt="BTHD"
                    style={{ height: 20, width: 'auto', filter: 'brightness(0) invert(1)' }}
                  />
                  <div style={{
                    background: 'rgba(191,30,46,0.12)', border: '1px solid #bf1e2e',
                    borderRadius: 20, padding: '3px 10px',
                    fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700,
                    fontSize: 9, color: RED, letterSpacing: 2,
                  }}>EARLY PATHWAYS</div>
                </div>

                {/* Athlete name + stage */}
                <div style={{ marginBottom: 20, paddingBottom: 20, borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                  <div style={{
                    fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900,
                    fontSize: 26, color: '#fff', letterSpacing: 0.5, lineHeight: 1, marginBottom: 6,
                  }}>
                    {fullName.toUpperCase()}
                  </div>
                  <div style={{
                    fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700,
                    fontSize: 11, color: stageColor, letterSpacing: 2,
                  }}>
                    STAGE 4 · NIL AWARENESS AND READINESS
                  </div>
                </div>

                {/* Milestones */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 9, color: DIM, letterSpacing: 2, fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, marginBottom: 10 }}>
                    WHAT YOU BUILT
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {PROFILE_UNLOCKS[4].flatMap((weekUnlocks, wIdx) =>
                      weekUnlocks.map((unlock, tIdx) => (
                        <span key={`${wIdx}-${tIdx}`} style={{
                          fontSize: 10, padding: '3px 10px', borderRadius: 20,
                          background: `rgba(1,76,141,0.18)`,
                          border: '1px solid rgba(1,76,141,0.45)',
                          color: stageColor,
                          fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, letterSpacing: 0.5,
                        }}>
                          {unlock[0]}
                        </span>
                      ))
                    )}
                  </div>
                </div>

                {/* Personal brand statement */}
                {(() => {
                  const brandStatement = responses.find(r => r.stage === 4 && r.week === 3 && r.task_order === 1)?.response;
                  return brandStatement ? (
                    <div style={{ marginBottom: 20, paddingTop: 18, borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                      <div style={{ fontSize: 9, color: DIM, letterSpacing: 2, fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, marginBottom: 10 }}>
                        YOUR PERSONAL BRAND
                      </div>
                      <div style={{
                        fontSize: 13, color: 'rgba(255,255,255,0.8)',
                        fontStyle: 'italic', lineHeight: 1.65,
                        borderLeft: `3px solid ${stageColor}`, paddingLeft: 14,
                      }}>
                        "{brandStatement.length > 160 ? brandStatement.slice(0, 160) + '…' : brandStatement}"
                      </div>
                    </div>
                  ) : null;
                })()}

                {/* Card footer */}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.07)',
                }}>
                  <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.25)', letterSpacing: 0.5 }}>
                    The game ends. The journey does not.
                  </div>
                  <div style={{
                    fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800,
                    fontSize: 10, color: stageColor, letterSpacing: 1.5,
                  }}>
                    NIL READY
                  </div>
                </div>
              </div>

              {/* Download button */}
              <button
                onClick={handleDownloadCard}
                disabled={downloadingCard}
                style={{
                  padding: '11px 24px', borderRadius: 8, marginBottom: 16,
                  background: 'none', border: `1px solid rgba(1,76,141,0.5)`,
                  color: stageColor, fontSize: 12, cursor: downloadingCard ? 'default' : 'pointer',
                  fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, letterSpacing: 1,
                }}
              >
                {downloadingCard ? 'Saving…' : 'DOWNLOAD YOUR CARD'}
              </button>

              <div style={{ marginBottom: 24 }}>
                <a
                  href="mailto:?subject=BTHD Behavioural Program Access&body=I have completed Early Pathways Stage 4 and would like to unlock the BTHD Behavioural Program."
                  style={{
                    display: 'inline-block', padding: '14px 28px', borderRadius: 10,
                    background: RED, color: '#fff', textDecoration: 'none',
                    fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700,
                    fontSize: 14, letterSpacing: 1,
                  }}
                >
                  Contact your organization to unlock your next stage
                </a>
              </div>

              <div>
                <button onClick={handleLogout} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.2)', fontSize: 12, cursor: 'pointer', fontFamily: 'Barlow, sans-serif' }}>Sign out</button>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 15, color: DIM, lineHeight: 1.7, marginBottom: 28 }}>
                {fullName}, you have completed all nine reflections in your Early Pathways program.
                You have put real words to who you are and who you are becoming.
              </div>
              <div style={{ height: 3, borderRadius: 2, background: `linear-gradient(to right, ${RED}, ${stageColor})`, marginBottom: 28 }} />
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)' }}>The game ends. The journey does not.</div>
              <button
                onClick={() => { setReviewMode(true); setActiveWeek(1); setActiveTaskOrder(1); }}
                style={{ marginTop: 28, background: 'none', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: DIM, fontSize: 13, padding: '10px 20px', cursor: 'pointer', fontFamily: 'Barlow, sans-serif' }}
              >
                Review my responses
              </button>
              <div style={{ marginTop: 14 }}>
                <button onClick={handleLogout} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.2)', fontSize: 12, cursor: 'pointer', fontFamily: 'Barlow, sans-serif' }}>Sign out</button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ─── Unlock moment ────────────────────────────────────────────────────────

  const UnlockScreen = currentUnlock && (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '48px 32px', textAlign: 'center',
    }}>
      <div style={{
        fontSize: 10, color: stageColor,
        fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: 3,
        marginBottom: 36,
      }}>
        {currentUnlock.type === 'trait' ? 'TRAIT UNLOCKED' : 'ADDED TO YOUR PROFILE'}
      </div>

      {currentUnlock.type === 'trait' ? (
        // Badge card
        <div style={{
          width: 160, height: 160, borderRadius: 24,
          background: `rgba(${rgb}, 0.1)`,
          border: `2px solid ${stageColor}`,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          marginBottom: 28, padding: '0 16px',
        }}>
          <div style={{ width: 24, height: 3, borderRadius: 2, background: stageColor, marginBottom: 14 }} />
          <div style={{
            fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900,
            fontSize: currentUnlock.title.length > 12 ? 18 : 22,
            color: stageColor, textAlign: 'center', lineHeight: 1.15, letterSpacing: 0.5,
          }}>
            {currentUnlock.title.toUpperCase()}
          </div>
          <div style={{ width: 24, height: 3, borderRadius: 2, background: stageColor, marginTop: 14 }} />
        </div>
      ) : (
        // Profile section added card
        <div style={{
          maxWidth: 440, width: '100%',
          background: SURF, borderRadius: 14, padding: '20px 24px',
          border: `1px solid rgba(${rgb}, 0.3)`,
          borderLeft: `4px solid ${stageColor}`,
          marginBottom: 24, textAlign: 'left',
        }}>
          <div style={{
            fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800,
            fontSize: 14, color: stageColor, letterSpacing: 1, marginBottom: 10,
          }}>
            {currentUnlock.title.toUpperCase()}
          </div>
          {currentUnlock.responsePreview && (
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, fontStyle: 'italic' }}>
              "{currentUnlock.responsePreview}"
            </div>
          )}
        </div>
      )}

      <div style={{
        fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700,
        fontSize: currentUnlock.type === 'trait' ? 20 : 15,
        color: '#fff', marginBottom: 8,
      }}>
        {currentUnlock.type === 'trait' ? currentUnlock.title : ''}
      </div>

      <div style={{ fontSize: 14, color: DIM, lineHeight: 1.6, maxWidth: 340, marginBottom: 40 }}>
        {currentUnlock.description}
      </div>

      {/* Progress counter */}
      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.25)', marginBottom: 32 }}>
        {totalComplete} of 9 complete
      </div>

      <button
        onClick={handleContinue}
        style={{
          padding: '14px 40px', borderRadius: 10,
          background: stageColor, border: 'none', color: '#fff',
          fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700,
          fontSize: 15, letterSpacing: 1.5, cursor: 'pointer',
        }}
      >
        {continueLabel}
      </button>
    </div>
  );

  // ─── Prompt screen ────────────────────────────────────────────────────────

  const PromptScreen = (
    <div style={{ flex: 1, padding: '32px', maxWidth: 720, width: '100%', boxSizing: 'border-box' }}>

      {/* Progress track */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 10 }}>
          {stageData.weeks.map((w, wIdx) => (
            <React.Fragment key={w.week}>
              {wIdx > 0 && (
                <div style={{
                  flex: 1, height: 1,
                  background: isWeekUnlocked(w.week) ? `rgba(${rgb}, 0.25)` : 'rgba(255,255,255,0.05)',
                }} />
              )}
              <div>
                <div style={{
                  fontSize: 9, color: w.week === activeWeek ? stageColor : DIM,
                  fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: 1.5,
                  marginBottom: 6, textAlign: 'center',
                }}>
                  WEEK {w.week}
                </div>
                <div style={{ display: 'flex', gap: 5, justifyContent: 'center' }}>
                  {[1, 2, 3].map(t => {
                    const done = isTaskComplete(w.week, t);
                    const isCurrent = w.week === activeWeek && t === activeTaskOrder && !done;
                    return (
                      <div key={t} style={{
                        width: isCurrent ? 14 : 10, height: 10, borderRadius: 5,
                        background: done ? stageColor :
                          isCurrent ? `rgba(${rgb}, 0.35)` : 'rgba(255,255,255,0.08)',
                        border: isCurrent ? `1.5px solid ${stageColor}` : 'none',
                        transition: 'all 0.2s',
                      }} />
                    );
                  })}
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.25)' }}>
          <span style={{ color: '#fff', fontWeight: 600 }}>{totalComplete}</span> of 9 complete
        </div>
      </div>

      {/* Week + question label */}
      <div style={{ marginBottom: 16 }}>
        <div style={{
          fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700,
          fontSize: 11, letterSpacing: 2, color: stageColor, marginBottom: 4,
        }}>
          {weekData.title.toUpperCase()} · QUESTION {activeTaskOrder} OF 3
        </div>
      </div>

      {/* Prompt — large text, especially for Stage 1/2 */}
      <div style={{
        borderLeft: `3px solid ${stageColor}`, paddingLeft: 20, marginBottom: 28,
      }}>
        <div style={{
          fontSize: promptFontSize, color: 'rgba(255,255,255,0.9)',
          lineHeight: 1.55, fontWeight: epStage <= 2 ? 500 : 400,
        }}>
          {currentPrompt}
        </div>
      </div>

      {/* Response area */}
      {!reviewMode && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: DIM, fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: 1.5, marginBottom: 10 }}>
            {epStage === 1 ? 'WRITE YOUR ANSWER' : 'YOUR RESPONSE'}
          </div>
          <textarea
            value={draftText}
            onChange={e => { setDraftText(e.target.value); if (savedThisTask) setSavedThisTask(false); }}
            placeholder={epStage === 1 ? 'Write your answer here…' : 'Write your response here…'}
            rows={epStage <= 2 ? 5 : 6}
            style={{
              width: '100%', padding: '16px', borderRadius: 10,
              border: `1px solid ${draftText.length > 10 ? stageColor : BORD}`,
              background: 'rgba(255,255,255,0.04)',
              color: '#fff', fontSize: epStage <= 2 ? 15 : 14, lineHeight: 1.6,
              outline: 'none', resize: 'vertical', boxSizing: 'border-box',
              fontFamily: 'Barlow, sans-serif', transition: 'border-color 0.2s',
            }}
          />
        </div>
      )}

      {/* Review mode response */}
      {reviewMode && (
        <div style={{
          background: SURF, borderRadius: 10, padding: '16px 20px',
          border: `1px solid ${BORD}`, marginBottom: 20,
          fontSize: 14, color: 'rgba(255,255,255,0.75)', lineHeight: 1.7,
        }}>
          {draftText || <span style={{ color: 'rgba(255,255,255,0.2)', fontStyle: 'italic' }}>No response recorded.</span>}
        </div>
      )}

      {/* Actions — save flow */}
      {!reviewMode && (
        <div>
          {savedThisTask ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 13, color: stageColor, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>✓</span><span>Saved</span>
              </div>
              {!(isLastTask && isLastWeek) && (
                <button
                  onClick={handleAdvance}
                  style={{
                    padding: '11px 22px', borderRadius: 10,
                    background: 'none', border: `1px solid rgba(${rgb}, 0.4)`,
                    color: stageColor,
                    fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700,
                    fontSize: 13, letterSpacing: 1, cursor: 'pointer',
                  }}
                >
                  {isLastTask ? 'NEXT WEEK →' : 'NEXT QUESTION →'}
                </button>
              )}
            </div>
          ) : (
            <button
              onClick={handleSave}
              disabled={!canSave || saving}
              style={{
                padding: '14px 36px', borderRadius: 10,
                background: canSave && !saving ? stageColor : 'rgba(255,255,255,0.08)',
                border: 'none', color: canSave && !saving ? '#fff' : DIM,
                fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700,
                fontSize: 15, letterSpacing: 1,
                cursor: canSave && !saving ? 'pointer' : 'default',
                transition: 'all 0.2s',
              }}
            >
              {saving ? 'Saving…' : 'SAVE RESPONSE'}
            </button>
          )}
        </div>
      )}

      {/* Review nav */}
      {reviewMode && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {activeTaskOrder > 1 && (
            <button
              onClick={() => setActiveTaskOrder(t => t - 1)}
              style={{ padding: '11px 20px', borderRadius: 10, background: 'rgba(255,255,255,0.06)', border: `1px solid ${BORD}`, color: DIM, fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, fontSize: 13, letterSpacing: 1, cursor: 'pointer' }}
            >
              PREVIOUS
            </button>
          )}
          {(activeTaskOrder < 3 || activeWeek < 3) && (
            <button
              onClick={() => {
                if (activeTaskOrder < 3) setActiveTaskOrder(t => t + 1);
                else { setActiveWeek(w => w + 1); setActiveTaskOrder(1); }
              }}
              style={{ padding: '11px 20px', borderRadius: 10, background: 'rgba(255,255,255,0.06)', border: `1px solid ${BORD}`, color: DIM, fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, fontSize: 13, letterSpacing: 1, cursor: 'pointer' }}
            >
              NEXT →
            </button>
          )}
          <button
            onClick={() => setReviewMode(false)}
            style={{ padding: '11px 20px', borderRadius: 10, background: 'none', border: `1px solid rgba(${rgb}, 0.3)`, color: stageColor, fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, fontSize: 13, letterSpacing: 1, cursor: 'pointer' }}
          >
            BACK TO COMPLETION
          </button>
        </div>
      )}
    </div>
  );

  // ─── Main portal layout ────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: BG, fontFamily: 'Barlow, sans-serif' }}>

      {/* Sidebar — desktop */}
      <div style={{ display: 'none' }} className="ep-sidebar-desktop">{SidebarContent}</div>
      <style>{`
        @media (min-width: 768px) {
          .ep-sidebar-desktop { display: flex !important; }
          .ep-mobile-header { display: none !important; }
        }
      `}</style>

      {/* Mobile header */}
      <div className="ep-mobile-header" style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: SURF, borderBottom: `1px solid ${BORD}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img
            src="/bthd-email-sig-300x80-transparent.png"
            alt="Bigger Than Hoop Dreams"
            style={{ height: 20, width: 'auto', filter: 'brightness(0) invert(1)' }}
          />
          <div style={{
            display: 'inline-flex', alignItems: 'center',
            background: 'rgba(191,30,46,0.08)', border: `1px solid ${RED}`,
            borderRadius: 20, padding: '2px 10px',
          }}>
            <span style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, fontSize: 10, color: RED, letterSpacing: 1.5 }}>EARLY PATHWAYS</span>
          </div>
        </div>
        <button onClick={() => setSidebarOpen(o => !o)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>
          {sidebarOpen ? '✕' : '☰'}
        </button>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200 }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 260, height: '100%', display: 'flex', flexDirection: 'column' }}>
            {SidebarContent}
          </div>
        </div>
      )}

      {/* Main */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', paddingTop: 0 }}>

        {/* Header bar */}
        <div style={{ borderBottom: `1px solid ${BORD}`, padding: '20px 32px', background: SURF }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                <span style={{
                  fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, fontSize: 11,
                  letterSpacing: 2, color: stageColor,
                  background: `rgba(${rgb}, 0.15)`, padding: '3px 8px', borderRadius: 4,
                }}>STAGE {epStage}</span>
                {reviewMode && (
                  <span style={{ fontSize: 11, color: '#fff', background: `rgba(${rgb}, 0.2)`, padding: '2px 8px', borderRadius: 4, fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700, letterSpacing: 1 }}>
                    REVIEWING
                  </span>
                )}
              </div>
              <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 24, color: '#fff', letterSpacing: 0.5 }}>
                {stageData.name}
              </div>
              <div style={{ fontSize: 13, color: DIM, marginTop: 2 }}>Ages {stageData.ages}</div>
            </div>
          </div>
        </div>

        {/* Content: unlock moment or prompt */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {showUnlock && currentUnlock ? UnlockScreen : PromptScreen}
        </div>
      </div>
    </div>
  );
}
